import React from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { getImageUrlWithEnv } from '../utils/imageUrl';

const TableStandings = ({ standings = [], title = "სატურნირო ცხრილი", onRefresh }) => {
  if (standings.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 font-georgian">{title}</h2>
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="text-gray-500 hover:text-primary-600 font-medium font-georgian flex items-center"
              title="განახლება"
            >
              <ArrowPathIcon className="h-4 w-4 mr-1" />
              განახლება
            </button>
          )}
        </div>
        <div className="text-center py-8">
          <p className="text-gray-500 font-georgian">სატურნირო ცხრილი არ არის ხელმისაწვდომი</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 font-georgian">{title}</h2>
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="text-gray-500 hover:text-primary-600 font-medium font-georgian flex items-center"
            title="განახლება"
          >
            <ArrowPathIcon className="h-4 w-4 mr-1" />
            განახლება
          </button>
        )}
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                ადგ.
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                კლუბი
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                თამაში
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                მოგება
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                ფრე
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                წაგება
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                გატანილი
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                მიღებული
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                ±
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                ქულა
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {standings.map((team, index) => (
              <tr 
                key={team.id} 
                className={`hover:bg-gray-50 ${
                  team.isCurrentTeam ? 'bg-primary-50 border-l-4 border-primary-500' : ''
                }`}
              >
                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {index + 1}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-8 w-8">
                      <img
                        className="h-8 w-8 rounded-full object-contain"
                        src={getImageUrlWithEnv(team.logo)}
                        alt={team.name}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-bold text-sm" style={{display: 'none'}}>
                        {team.name.charAt(0)}
                      </div>
                    </div>
                    <div className="ml-3">
                      <div className={`text-sm font-medium ${
                        team.isCurrentTeam ? 'text-primary-900' : 'text-gray-900'
                      } font-georgian`}>
                        {team.name}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                  {team.played}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                  {team.won}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                  {team.drawn}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                  {team.lost}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                  {team.goalsFor}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                  {team.goalsAgainst}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-center">
                  <span className={team.goalsFor - team.goalsAgainst >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {team.goalsFor - team.goalsAgainst >= 0 ? '+' : ''}{team.goalsFor - team.goalsAgainst}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-center">
                  {team.points}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="mt-4 text-xs text-gray-500 font-georgian">
        <p>ადგ. - ადგილი, თამაში - თამაშები, მოგება - მოგებული, ფრე - ფრე, წაგება - წაგებული, გატანილი - გატანილი გოლები, მიღებული - მიღებული გოლები, ± - სხვაობა, ქულა - ქულები</p>
      </div>
    </div>
  );
};

export default TableStandings;
